import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { keysToCamelCase } from '@hotjar/webapp/lib/convert-string';
import { LEGACY_SEGMENT_TYPE } from '@hotjar/webapp/domains/segments/constant';
import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import type {
  QueryFormatFilter,
  QueryLeaf,
  QueryListFilter,
  QueryListOperandEnum,
  QueryMapOperandEnum,
} from '@hotjar/global-filters';
import type { SiteId } from '@hotjar/site';
import type { RecordingId, WorthWatchingAnswer } from '../model';
import { mapRecordingsForWidgetResponseDtoToModel } from '../mapper';
import type { Recording } from '../types';

/* eslint-disable camelcase */
interface GenericSuccessData {
  success: true;
}
/* eslint-enable camelcase */

// NOTE: we always want to use secure API, even when the player is loaded over HTTP. Axios automatically sets the
// protocol to https for us.
const API_ROOT = `/api/v1/sites`;
const API_ROOT_V3 = '/api/v3/sites';

function createEndpointPath(siteId: SiteId, recordingId: RecordingId): string {
  return `${API_ROOT}/${siteId}/recordings/${recordingId}`;
}

export const getFilters = (segment: Segment | null): QueryFormatFilter => {
  const DEFAULT_DAYS_AGO = { DAYS_AGO: { created: 0 } };
  const ONE_YEAR_AGO = { DAYS_AGO: { created: 365 } };

  const selectedTimespan = segment?.id === -1 ? DEFAULT_DAYS_AGO : ONE_YEAR_AGO;

  if (!segment) {
    return DEFAULT_DAYS_AGO;
  }

  return (
    Object.keys(segment?.filters || {}) as (QueryListOperandEnum | QueryMapOperandEnum)[]
  ).reduce(
    (result: Segment['filters'], key: QueryListOperandEnum | QueryMapOperandEnum) => {
      // if DAYS_AGO at root level
      // override to selectedTimespan
      if (key === 'DAYS_AGO') {
        return { ...result, [key]: selectedTimespan.DAYS_AGO };
      }

      return {
        ...result,
        [key]: (segment?.filters as QueryListFilter)[key as QueryListOperandEnum]?.map((filter) => {
          // if DAYS_AGO nested inside another key
          // override to selectedTimespan
          if ((filter as QueryLeaf).DAYS_AGO) {
            return selectedTimespan;
          }
          return filter;
        }),
      };
    },
    { ...segment?.filters },
  );
};

export async function getRecordingsForWidget(
  siteId: SiteId,
  segment: Segment | null,
  stateName: string,
): Promise<Recording[]> {
  const body = {
    ...(segment
      ? {
          segment_id: segment?.id,
          segment_type: LEGACY_SEGMENT_TYPE,
          state_name: stateName,
          tool: 'Recordings',
          url: window.location.pathname + window.location.search,
        }
      : {}),
    size: 3,
    sort_by: '-relevance_score',
    tz_offset: 60,
    source: 'overview-widget',
    global_filters: JSON.stringify(getFilters(segment)),
  };

  const response = await axios.post(`${API_ROOT_V3}/${siteId}/recordings`, body);

  return mapRecordingsForWidgetResponseDtoToModel(response.data);
}

export async function setWorthWatching(
  siteId: SiteId,
  recordingId: RecordingId,
  worthWatching: WorthWatchingAnswer,
): Promise<AxiosResponse<GenericSuccessData>> {
  const path = createEndpointPath(siteId, recordingId);
  const body = {
    action: 'set_worth_watching',
    worth_watching: worthWatching,
  };
  return axios.post(path, body);
}

export async function toggleShared(
  siteId: SiteId,
  recordingId: RecordingId,
): Promise<AxiosResponse<GenericSuccessData>> {
  const path = `${API_ROOT_V3}/${siteId}/recording-id/${recordingId}/share`;
  return axios.post(path);
}

export const IN_PROGRESS_SESSION_QUERY_KEY = 'in_progress_sessions';

export const sessionsInProgress = async (
  siteId: SiteId,
  selectRandom = false,
): Promise<AxiosResponse<GenericSuccessData>> => {
  const response = await axios.get(`${API_ROOT}/${siteId}/sessions-in-progress`, {
    params: {
      select_random: selectRandom ? 1 : null,
    },
  });
  return keysToCamelCase(response?.data, true);
};

export interface RecordingUrl {
  url: string;
  // eslint-disable-next-line camelcase
  response_id: number;
}

export interface RecordingUrls {
  recordings: RecordingUrl[];
}

export const getRecordingUrl = async (siteId: SiteId, tag: string): Promise<string | undefined> => {
  const {
    data: {
      recordings: [firstRecording],
    },
  } = await axios.post<RecordingUrls>(`${API_ROOT_V3}/${siteId}/recordings`, {
    // eslint-disable-next-line camelcase
    global_filters: JSON.stringify({
      // eslint-disable-next-line camelcase
      EQUAL: { autotag_names: tag },
    }),
    source: 'feedback-widget',
    size: 1,
  });

  return firstRecording?.url;
};

export const getRecordingUrls = async (
  siteId: SiteId,
  tags: string[],
  createdDaysAgo: number,
): Promise<RecordingUrl[]> => {
  const response = await axios.post<RecordingUrl[]>(
    `/api/v1/sites/${siteId}/recordings/feedback-responses`,
    {
      autotag_names: tags,
      created: createdDaysAgo,
    },
  );

  return response.data;
};
